import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ChevronRightIcon,
  LocationIcon,
  ScheduleIcon
} from '@toasttab/buffet-pui-icons'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection'
import { formatLocationAddress } from 'public/components/online_ordering/addressUtils'
import { formatDeliveryAddress } from 'src/public/components/online_ordering/address_configs'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { ToggleInput } from 'shared/components/common/forms'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'shared/js/locationUtils'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { Channel } from 'public/js/siteUtilities'
import { LocationMap } from 'public/components/default_template/schedule_map'
import FulfillmentTimeSummary from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeSummary'
import FulfillmentTimeModal from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeModal'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import Skeleton from 'react-loading-skeleton'
import DeliveryModal from 'public/components/default_template/online_ordering/delivery_address_modal/DeliveryModal'
import { t } from 'src/translations'
import {
  trackDiningOptionChangedV2,
  TrackingSource
} from 'public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import InputField from 'shared/components/common/form_input/InputField'
import useTracker from 'src/lib/js/hooks/useTracker'
import { useFormContext } from 'react-hook-form'
import { useFlags } from 'launchdarkly-react-client-sdk'
import Image from 'shared/components/common/Image'

const CheckoutFulfillmentSection = () => {
  const { ooRestaurant, selectedLocation } = useRestaurant()
  const { cart, channel, loadingCart } = useCart()
  const {
    setDeliveryModalOpen,
    deliveryModalOpen,
    deliveryModalForcedScreen,
    sessionAddressLabel,
    setSessionAddressLabel,
    savedAddressUsed,
    lastKnownValidDeliveryAddress,
    setSaveSessionAddress,
    saveSessionAddress
  } = useDelivery()
  const {
    canOrderTakeout,
    canOrderDelivery,
    setSelectedDiningOptionBehavior,
    fulfillmentData,
    updateCartFulfillment,
    loadingFulfillment
  } = useFulfillment()
  const [curbside, setCurbside] = useState(false)
  const tracker = useTracker()
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  const { contactlessDelivery, setContactlessDelivery } = useCheckout()
  const { customer } = useCustomer()

  const [returnFocusElementId, setReturnFocusElementId] = useState<
    string | undefined
  >()

  const diningOption = cart?.diningOptionBehavior

  const oneDiningOption = canOrderTakeout !== canOrderDelivery

  const showCurbside = useMemo(
    () =>
      (ooRestaurant?.curbsidePickupConfig?.enabled ||
        ooRestaurant?.curbsidePickupConfig?.enabledV2) &&
      cart?.diningOptionBehavior === DiningOptionBehavior.TakeOut &&
      channel !== Channel.ECOMM,
    [ooRestaurant?.curbsidePickupConfig, cart?.diningOptionBehavior, channel]
  )

  const switchDiningOption = useCallback(async () => {
    const newDiningOption =
      diningOption === DiningOptionBehavior.TakeOut
        ? DiningOptionBehavior.Delivery
        : DiningOptionBehavior.TakeOut

    // switching to delivery requires address collection completion, open modal to begin that process only.
    if (newDiningOption === DiningOptionBehavior.Delivery) {
      if (
        lastKnownValidDeliveryAddress &&
        fulfillmentData?.cartFulfillmentData
      ) {
        await updateCartFulfillment({
          fulfillmentType: fulfillmentData.cartFulfillmentData.fulfillmentType,
          fulfillmentDateTime:
            fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
          diningOptionBehavior: DiningOptionBehavior.Delivery,
          deliveryInfo: lastKnownValidDeliveryAddress
        })
        // TODO: Add dining optiong changed V2 track event here
        return
      }
      setDeliveryModalOpen(true)
      // on confirm/close of delivery modal, return focus to this button
      setReturnFocusElementId('switch-dining-option-button')
      return
    }

    setSelectedDiningOptionBehavior(newDiningOption)
    if (fulfillmentData?.cartFulfillmentData) {
      await updateCartFulfillment({
        fulfillmentType: fulfillmentData.cartFulfillmentData.fulfillmentType,
        fulfillmentDateTime:
          fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
        diningOptionBehavior: newDiningOption,
        deliveryInfo: fulfillmentData.cartFulfillmentData.deliveryInfo
      })
      trackDiningOptionChangedV2(
        tracker,
        fulfillmentData.cartFulfillmentData.fulfillmentType,
        false,
        TrackingSource.Checkout,
        newDiningOption,
        fulfillmentData.cartFulfillmentData.deliveryInfo
      )
    }
  }, [
    diningOption,
    setSelectedDiningOptionBehavior,
    fulfillmentData?.cartFulfillmentData,
    lastKnownValidDeliveryAddress,
    setDeliveryModalOpen,
    updateCartFulfillment,
    tracker
  ])

  const [trackingSource, setTrackingSource] = useState<TrackingSource>(
    TrackingSource.Checkout
  )

  const isPickup = useMemo(
    () => diningOption === DiningOptionBehavior.TakeOut,
    [diningOption]
  )
  const isDelivery = useMemo(
    () => diningOption === DiningOptionBehavior.Delivery,
    [diningOption]
  )
  const diningOptionText = isPickup
    ? t('fulfillment-checkout-section.pickup')
    : t('fulfillment-checkout-section.delivery')

  // Open the delivery flow modal if this is a delivery order and there is no delivery info
  useEffect(() => {
    if (
      isDelivery &&
      !fulfillmentData?.cartFulfillmentData.deliveryInfo &&
      !loadingFulfillment &&
      !loadingCart
    ) {
      setDeliveryModalOpen(true)
      setTrackingSource(TrackingSource.CheckoutBlockerModal)
    } else {
      setTrackingSource(TrackingSource.Checkout)
    }
  }, [
    fulfillmentData?.cartFulfillmentData.deliveryInfo,
    isDelivery,
    loadingFulfillment,
    loadingCart,
    setDeliveryModalOpen,
    setTrackingSource
  ])

  const isLoading = useMemo(() => {
    return loadingFulfillment || loadingCart
  }, [loadingFulfillment, loadingCart])

  if (!ooRestaurant || !cart?.restaurant) {
    return null
  }
  const restaurantName = getDisplayBusinessName(
    ooRestaurant.whiteLabelName || cart.restaurant.name,
    selectedLocation?.name
  )
  const restaurantAddress = formatLocationAddress(cart.restaurant.location)

  const deliveryAddress = fulfillmentData?.cartFulfillmentData?.deliveryInfo
  const deliveryAddressDisplay = deliveryAddress
    ? formatDeliveryAddress(
        deliveryAddress,
        {
          full: true,
          country: ooRestaurant.i18n.country
        },
        intlAddressCountryLogicErrorHandling
      )
    : ''

  const showSaveAddress = !!(customer && !savedAddressUsed)

  return (
    <CheckoutSection
      title={t('fulfillment-checkout-section.dining-option-details', {
        diningOption: diningOptionText
      })}
      secondaryHeaderContent={
        !oneDiningOption && (
          <>
            {isLoading ? (
              <Skeleton
                containerClassName='loadingSkeleton'
                className='loadingSkeleton'
                containerTestId='switch-dining-option-loading-skeleton'
              />
            ) : (
              <button
                onClick={switchDiningOption}
                type='button'
                className='switchDiningOptionButton'
                id='switch-dining-option-button'
              >
                {t('fulfillment-checkout-section.switch-to-dining-option', {
                  diningOption: isPickup
                    ? t('fulfillment-checkout-section.delivery')
                    : t('fulfillment-checkout-section.pickup')
                })}
              </button>
            )}
          </>
        )
      }
      className='checkoutFulfillmentSection'
    >
      <div role='group' tabIndex={0}>
        {isPickup && (
          <LocationMap
            name={cart.restaurant.name}
            address1={cart.restaurant.location.address1}
            address2={cart.restaurant.location.address2}
            city={cart.restaurant.location.city}
            state={cart.restaurant.location.state}
            zipcode={cart.restaurant.location.zip}
            alwaysShowMap={true}
            trackerContext='fulfillment_checkout'
          />
        )}
        <ul>
          <FulfillmentSectionListItem>
            {isLoading ? (
              <Skeleton
                className='loadingSkeleton'
                containerClassName='loadingSkeleton'
                containerTestId='fulfillment-time-loading-skeleton'
                borderRadius={8}
              />
            ) : (
              <>
                {/* Fulfillment time summary for Pickup and Delivery */}
                <ScheduleIcon size='xs' accessibility='decorative' />
                <FulfillmentTimeSummary
                  showIcon={false}
                  subtitleText={t(
                    'fulfillment-checkout-section.estimated-time',
                    {
                      diningOption: diningOptionText.toLowerCase()
                    }
                  )}
                />
              </>
            )}
          </FulfillmentSectionListItem>
          {isLoading ? (
            <FulfillmentSectionListItem>
              <Skeleton
                className='loadingSkeleton'
                containerClassName='loadingSkeleton'
                containerTestId='location-loading-skeleton'
                borderRadius={8}
              />
            </FulfillmentSectionListItem>
          ) : (
            <FulfillmentSectionListItem interactable={isPickup}>
              {isPickup ? (
                <>
                  {/* Restaurant details */}
                  <Image src='/icons/walking.svg' alt='' />
                  <div>
                    {restaurantName}
                    <span className='subtitleText'>{restaurantAddress}</span>
                  </div>
                </>
              ) : (
                // Delivery address details
                <button
                  id='delivery-address-details-button'
                  type='button'
                  onClick={() => {
                    setDeliveryModalOpen(true, 'address-confirm')
                    setReturnFocusElementId('delivery-address-details-button')
                  }}
                  data-testid='delivery-address-details'
                  className='deliveryAddressButton'
                >
                  <LocationIcon size='xs' accessibility='decorative' />
                  <div>
                    <span className='deliveryAddressSummary bold'>
                      {deliveryAddressDisplay}
                    </span>
                    <span className='subtitleText'>
                      {t('fulfillment-checkout-section.edit-delivery-details')}
                    </span>
                  </div>
                  <ChevronRightIcon
                    size='xs'
                    accessibility='semantic'
                    aria-label={t(
                      'fulfillment-checkout-section.edit-delivery-details'
                    )}
                  />
                </button>
              )}
            </FulfillmentSectionListItem>
          )}
          {/* Curbside pickup details */}
          {showCurbside && (
            <FulfillmentSectionListItem>
              <TakeoutDetails curbside={curbside} setCurbside={setCurbside} />
            </FulfillmentSectionListItem>
          )}
          {/* Contactless delivery details */}
          {isDelivery && (
            <FulfillmentSectionListItem>
              <div className='deliveryCheckboxContainer'>
                {/* Save address checkbox */}
                {showSaveAddress && (
                  <>
                    <ToggleInput
                      id='saveAddress'
                      type='checkbox'
                      name='saveAddress'
                      checked={saveSessionAddress}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setSaveSessionAddress(event.target.checked)
                      }
                      dataTestId='save-new-address'
                    >
                      {t('fulfillment-checkout-section.save-address')}
                    </ToggleInput>
                    <AnimatedSection
                      expanded={saveSessionAddress}
                      className='animated'
                    >
                      <div className='addressLabel'>
                        <InputField
                          id='addressLabel'
                          type='text'
                          value={sessionAddressLabel}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => setSessionAddressLabel(event.target.value)}
                          label={t('address-confirm-screen.address-label')}
                          required={saveSessionAddress}
                          preventFormSubmit
                        />
                      </div>
                    </AnimatedSection>
                  </>
                )}
                {/* Contactless delivery checkbox */}
                <ToggleInput
                  id='contactlessDelivery'
                  type='checkbox'
                  name='addContactlessDelivery'
                  checked={contactlessDelivery}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setContactlessDelivery(event.target.checked)
                  }
                >
                  {t('fulfillment-checkout-section.leave-it-at-my-door')}
                </ToggleInput>
              </div>
            </FulfillmentSectionListItem>
          )}
        </ul>
      </div>
      <FulfillmentTimeModal returnFocusElementId='fulfillment-time-summary-button' />
      <DeliveryModal
        isOpen={deliveryModalOpen}
        onClose={() => {
          setDeliveryModalOpen(false)
          setReturnFocusElementId(undefined)
        }}
        forceScreenPlacement={deliveryModalForcedScreen}
        forceTrackingSource={trackingSource}
        returnFocusElementId={returnFocusElementId}
      />
    </CheckoutSection>
  )
}

const FulfillmentSectionListItem = ({
  children,
  interactable
}: {
  children: React.ReactNode
  interactable?: boolean
}) => {
  return (
    <li tabIndex={interactable ? 0 : -1}>
      <div>{children}</div>
    </li>
  )
}

/** Render curbside pickup options details for takeout orders */
export const TakeoutDetails = ({
  curbside,
  setCurbside
}: {
  curbside: boolean
  setCurbside: (curbside: boolean) => void
}) => {
  const { cart, channel } = useCart()
  const { ooRestaurant } = useRestaurant()
  const { register } = useFormContext()
  const showCurbside = useMemo(
    () =>
      (ooRestaurant?.curbsidePickupConfig?.enabled ||
        ooRestaurant?.curbsidePickupConfig?.enabledV2) &&
      cart?.diningOptionBehavior === DiningOptionBehavior.TakeOut &&
      channel !== Channel.ECOMM,
    [ooRestaurant?.curbsidePickupConfig, cart?.diningOptionBehavior, channel]
  )
  if (
    cart?.diningOptionBehavior !== DiningOptionBehavior.TakeOut ||
    !cart?.restaurant?.location ||
    !ooRestaurant ||
    !cart?.order ||
    !showCurbside
  ) {
    return null
  }
  return (
    <div>
      <ToggleInput
        type='checkbox'
        id='curbsidePickup'
        dataTestId='curbsidePickupCheckbox'
        checked={curbside}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            setCurbside(!curbside)
          }
        }}
        {...register('curbsidePickup', {
          onChange: () => setCurbside(!curbside)
        })}
      >
        <div className='curbsideText' id='curbside-label'>
          <span className='bold'>
            {t('fulfillment-section.curbside-pickup')}
          </span>
          <span className='gray'>
            {t('fulfillment-section.bring-order-to-car')}
          </span>
        </div>
      </ToggleInput>
      <AnimatedSection
        expanded={Boolean(showCurbside && curbside)}
        testid='curbside-animated-section'
        className='animated'
      >
        <div className='curbsidePickupVehicle'>
          {/* max length values from
              https://github.com/toasttab/toast-orders/blob/main/toast-orders-application/src/main/java/com/toasttab/service/orders/resources/orika/mappers/CurbsidePickupMapper.kt */}
          <InputField
            className='vehicleInput'
            maxLength={100}
            id='curbsidePickupVehicle'
            label={t('fulfillment-section.vehicle-make-model')}
            required={curbside}
            preventFormSubmit
          />
          <InputField
            className='vehicleInput'
            maxLength={20}
            id='curbsidePickupVehicleColor'
            label={t('fulfillment-section.vehicle-color')}
            preventFormSubmit
          />
        </div>
      </AnimatedSection>
    </div>
  )
}

export default CheckoutFulfillmentSection
